import { useState } from 'react';

import { Link } from '@tanstack/react-router';

import { useQuery } from '@tanstack/react-query';

import {

  overviewQuery,

  domainsQuery,

  linksQuery,

  engagementQuery,

  percent,

  type Range,

} from '../lib/analytics.js';

import { TrendChart } from '../components/TrendChart.js';



const RANGES: Array<{ key: Range; label: string }> = [

  { key: '7d', label: '7 Tage' },

  { key: '30d', label: '30 Tage' },

  { key: '90d', label: '90 Tage' },

  { key: '365d', label: '1 Jahr' },

];



export function AnalyticsPage() {

  const [range, setRange] = useState<Range>('30d');



  const overview = useQuery(overviewQuery(range));

  const domains = useQuery(domainsQuery(range));

  const links = useQuery(linksQuery(range));

  const engagement = useQuery(engagementQuery(range));



  const totals = overview.data?.totals;

  const rates = overview.data?.rates;



  return (

    <div className="stack" style={{ gap: 'var(--space-6)' }}>

      <header className="page-header">

        <div>

          <h1 style={{ fontSize: 'var(--text-2xl)' }}>Auswertung</h1>

          <p className="muted text-sm">

            Alle Zahlen sind aus dem Ereignisstrom abgeleitet, nicht aus Zählerspalten. Maschinelle

            Öffnungen sind überall herausgerechnet.

          </p>

        </div>

        <div className="segmented" role="group" aria-label="Zeitraum">

          {RANGES.map((option) => (

            <button

              key={option.key}

              type="button"

              data-active={range === option.key ? 'true' : undefined}

              onClick={() => setRange(option.key)}

            >

              {option.label}

            </button>

          ))}

        </div>

      </header>



      {overview.isError && (

        <div className="alert" data-tone="danger" role="alert">

          {overview.error instanceof Error ? overview.error.message : 'Auswertung nicht ladbar.'}

        </div>

      )}



      {totals && rates && (

        <div className="stat-grid">

          <Stat label="Versendet" value={totals.sent.toLocaleString('de-CH')} />

          <Stat

            label="Öffnungsrate"

            value={percent(rates.openRate)}

            note={`${totals.opens.toLocaleString('de-CH')} eindeutige Öffnungen`}

          />

          <Stat

            label="Klickrate"

            value={percent(rates.clickRate)}

            note={`${totals.clicks.toLocaleString('de-CH')} eindeutige Klicks`}

          />

          <Stat

            label="Bounce-Rate"

            value={percent(rates.bounceRate)}

            note={totals.bounces > 0 ? `${totals.bounces} unzustellbar` : 'keine Rückläufer'}

          />

          <Stat

            label="Beschwerden"

            value={percent(rates.complaintRate, 2)}

            note="ab 0,1 % wird es kritisch"

          />

          <Stat

            label="Abmeldungen"

            value={percent(rates.unsubscribeRate)}

            note={`${totals.unsubscribes} im Zeitraum`}

          />

          <Stat

            label="Maschinell"

            value={totals.machineOpens.toLocaleString('de-CH')}

            note="gespeichert, aber nicht gezählt"

          />

        </div>

      )}



      <section className="card">

        <div className="card__body stack">

          <h2 style={{ fontSize: 'var(--text-lg)' }}>Verlauf</h2>

          {overview.data && <TrendChart points={overview.data.series} />}

        </div>

      </section>



      <section className="card">

        <div className="card__body stack">

          <h2 style={{ fontSize: 'var(--text-lg)' }}>Empfängerdomains</h2>

          {/*

            Ein Gesamtwert verdeckt genau das Interessante: 3 % Bounces über

            alles können heißen, dass ein Anbieter zu 40 % abweist.

          */}

          <p className="muted text-sm">

            Wo es klemmt, sieht man hier zuerst — nicht im Gesamtwert.

          </p>

          {domains.data && domains.data.items.length === 0 && (

            <p className="muted text-sm">Noch keine Versendungen im Zeitraum.</p>

          )}

          {domains.data && domains.data.items.length > 0 && (

            <div className="table-wrap">

              <table className="table">

                <thead>

                  <tr>

                    <th scope="col">Domain</th>

                    <th scope="col">Versendet</th>

                    <th scope="col">Öffnungsrate</th>

                    <th scope="col">Bounce-Rate</th>

                    <th scope="col">Beschwerden</th>

                  </tr>

                </thead>

                <tbody>

                  {domains.data.items.map((row) => (

                    <tr key={row.domain}>

                      <td>{row.domain}</td>

                      <td data-numeric>{row.sent.toLocaleString('de-CH')}</td>

                      <td data-numeric>{percent(row.openRate)}</td>

                      <td

                        data-numeric

                        data-warn={(row.bounceRate ?? 0) > 0.05 ? 'true' : undefined}

                      >

                        {percent(row.bounceRate)}

                      </td>

                      <td data-numeric>{row.complaints}</td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          )}

        </div>

      </section>



      <div className="detail-grid">

        <section className="card">

          <div className="card__body stack">

            <h2 style={{ fontSize: 'var(--text-lg)' }}>Meistgeklickte Links</h2>

            {links.data && links.data.items.length === 0 && (

              <p className="muted text-sm">Noch keine Klicks im Zeitraum.</p>

            )}

            <ul className="rank">

              {links.data?.items.slice(0, 10).map((item) => (

                <li key={item.url}>

                  <span className="rank__label" title={item.url}>

                    {item.url}

                  </span>

                  <span className="rank__value">

                    {item.clicks} × / {item.contacts} Person

                    {item.contacts === 1 ? '' : 'en'}

                  </span>

                </li>

              ))}

            </ul>

          </div>

        </section>



        <section className="card">

          <div className="card__body stack">

            <h2 style={{ fontSize: 'var(--text-lg)' }}>Aktivste Kontakte</h2>

            {engagement.data && engagement.data.items.length === 0 && (

              <p className="muted text-sm">Noch keine Aktivität im Zeitraum.</p>

            )}

            <ul className="rank">

              {engagement.data?.items.slice(0, 10).map((item) => (

                <li key={item.id}>

                  <Link to="/kontakte/$id" params={{ id: item.id }} className="rank__label">

                    {item.email}

                  </Link>

                  <span className="rank__value">

                    {item.clicks} Klicks · {item.opens} Öffnungen

                  </span>

                </li>

              ))}

            </ul>

          </div>

        </section>

      </div>

    </div>

  );

}



function Stat({ label, value, note }: { label: string; value: string; note?: string }) {

  return (

    <div className="stat">

      <span className="stat__label">{label}</span>

      <span className="stat__value" data-numeric>

        {value}

      </span>

      {note && <span className="stat__note">{note}</span>}

    </div>

  );

}

